import '@enterprise-ui/canvas-ui-css'
import { SideNav as CanvasSideNav } from '@enterprise-ui/canvas-ui-react'
import EnterpriseIcon from '@enterprise-ui/icons'
import type React from 'react'
import type { ReactNode } from 'react'
import { trackEvent } from '../../common/fireflyHelper'
import { StyledNavItemHeader } from './styles'

// Navbar item basic props
interface NavbarItemProps {
  selected?: boolean
  icon?: any
  label?: string
  featureFlag?: boolean | undefined
  as?: any
  to?: string
  isReactLink?: boolean | false
  otherProps?: any
}

// Navbar item as header
interface NavbarItemText {
  label?: string
  children?: ReactNode
}

type NavbarItem = NavbarItemText | NavbarItemProps

// Nested Navbar items
interface NavbarItems extends NavbarItemProps, NavbarItemText {
  nestedItems?: NavbarItem[]
}

export interface NavbarProps {
  header?: ReactNode
  footer?: ReactNode
  items?: NavbarItems[]
  settings?: any
}

// react-router-dom link
const ReactRouterLink: React.FC<NavbarItemProps> = (props) => {
  const { to, as, selected, icon, label, featureFlag, otherProps } = props
  const showItem = featureFlag === undefined || featureFlag === true
  const { onClick: itemOnClick, ...restOtherProps } = otherProps || {}
  const handleClick = (...args: unknown[]) => {
    trackEvent(`SideNav: ${label} clicked`)
    itemOnClick?.(...args)
  }
  return (
    <>
      {showItem && (
        <CanvasSideNav.NavigationItem
          to={to}
          as={as}
          selected={selected}
          {...restOtherProps}
          onClick={handleClick}
        >
          {icon && <EnterpriseIcon icon={icon} />}
          {label}
        </CanvasSideNav.NavigationItem>
      )}
    </>
  )
}

// next/anchor link or button
const NextRouterLink: React.FC<NavbarItemProps & { isMinimized: false }> = (
  props,
) => {
  const {
    to,
    as,
    selected,
    icon,
    label,
    featureFlag,
    otherProps,
    isMinimized,
  } = props
  const showItem = featureFlag === undefined || featureFlag === true
  const { onClick: itemOnClick, ...restOtherProps } = otherProps || {}
  const handleClick = (...args: unknown[]) => {
    trackEvent(`SideNav: ${label} clicked`)
    itemOnClick?.(...args)
  }

  // checking for button and minimized for adding additional icon padding
  const isMinimizedButton =
    typeof as === 'object' && isMinimized
      ? as?.render.toString().includes('Button')
      : false

  return (
    <>
      {showItem && (
        <CanvasSideNav.NavigationItem
          href={to}
          as={as}
          selected={selected}
          {...restOtherProps}
          onClick={handleClick}
          className={isMinimizedButton ? 'hc-pl-md' : ''}
        >
          {icon && <EnterpriseIcon icon={icon} />}
          {label}
        </CanvasSideNav.NavigationItem>
      )}
    </>
  )
}

// Text navbar item
const NavItemText: React.FC<NavbarItemText> = (props) => {
  const { children, label } = props
  return (
    <StyledNavItemHeader>
      {label ? (
        <span className="hc-ma-dense hc-pl-xs hc-fs-md">{label}</span>
      ) : (
        children
      )}
    </StyledNavItemHeader>
  )
}

export const SideNav: React.FC<NavbarProps> = (props) => {
  const { header, footer, items, settings } = props
  const { onMinimizeChange, ...restSettings } = settings || {}
  const handleMinimizeChange = (isMinimized: boolean, ...rest: unknown[]) => {
    trackEvent(isMinimized ? 'SideNav: Collapsed' : 'SideNav: Expanded')
    onMinimizeChange?.(isMinimized, ...rest)
  }

  return (
    <CanvasSideNav
      canMinimize
      {...restSettings}
      onMinimizeChange={handleMinimizeChange}
      data-testid="side-navbar"
    >
      {header && (
        // biome-ignore lint/a11y/useSemanticElements: not a semantic element
        <CanvasSideNav.Header as="button" role="button">
          {header}
        </CanvasSideNav.Header>
      )}
      <CanvasSideNav.Navigation>
        {items?.map((item, key) => (
          <div key={key}>
            {typeof item?.as === 'string' ? (
              <NavItemText {...item} />
            ) : item?.isReactLink ? (
              <ReactRouterLink {...item} />
            ) : (
              <NextRouterLink {...item} {...settings} />
            )}
          </div>
        ))}
      </CanvasSideNav.Navigation>
      {footer && <CanvasSideNav.Footer>{footer}</CanvasSideNav.Footer>}
    </CanvasSideNav>
  )
}
